import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { authAPI } from '../../services/api';
import './ProfilePage.scss';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const { t } = useSettings();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    avatar: null
  });
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        email: user.email || '',
        avatar: null
      });
      setPreviewAvatar(user.avatar);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, avatar: file }));
      setPreviewAvatar(URL.createObjectURL(file));
    }
  };

  const toggleEdit = () => {
    if (isEditing) {
      // Cancel logic: reset
      setFormData({
        first_name: user.first_name || '',
        email: user.email || '',
        avatar: null
      });
      setPreviewAvatar(user.avatar);
    }
    setIsEditing(!isEditing);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('first_name', formData.first_name);
      data.append('email', formData.email);
      if (formData.avatar) {
        data.append('avatar', formData.avatar);
      }

      await authAPI.updateProfile(data);
      window.location.reload(); 
    } catch (error) {
      console.error('Failed to update profile', error);
      alert('Ошибка при обновлении профиля');
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  if (!user) return <div className="profile-page container">Loading...</div>;

  return (
    <div className="profile-page">
      <div className="profile-header-bg"></div>
      
      <div className="container profile-container">
        <div className="profile-card">
          <div className="profile-header">
            <h1 className="profile-title">{t('profileTitle')}</h1>
            <div className={`status-badge ${user.is_superuser ? 'admin' : 'user'}`}>
              {user.is_superuser ? t('admin') : t('officialProfile')}
            </div>
          </div>

          <div className="profile-content">
            <div className="avatar-section">
              <div 
                className={`avatar-wrapper ${isEditing ? 'editable' : ''}`}
                onClick={handleAvatarClick}
              >
                <img 
                  src={previewAvatar || '/default-avatar.png'} 
                  alt="Profile" 
                  className="profile-avatar"
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'%3E%3C/path%3E%3Ccircle cx='12' cy='7' r='4'%3E%3C/circle%3E%3C/svg%3E";
                  }}
                />
                {isEditing && (
                  <div className="avatar-overlay">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  hidden 
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
              <p className="avatar-hint">
                {isEditing ? t('changeAvatar') : user.email}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-group">
                <label>{t('name')}</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder={t('name')}
                    className="presidential-input"
                  />
                  {!isEditing && <span className="locked-icon">🔒</span>}
                </div>
              </div>

              <div className="form-group">
                <label>{t('email')}</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder={t('email')}
                    className="presidential-input"
                  />
                  {!isEditing && <span className="locked-icon">🔒</span>}
                </div>
              </div>

              <div className="form-actions">
                {!isEditing ? (
                  <button type="button" className="presidential-btn edit-btn" onClick={toggleEdit}>
                    {t('edit')}
                  </button>
                ) : (
                  <>
                    <button type="button" className="presidential-btn cancel-btn" onClick={toggleEdit}>
                      {t('cancel')}
                    </button>
                    <button type="submit" className="presidential-btn save-btn" disabled={loading}>
                      {loading ? '...' : t('save')}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
